import {
  BundleType,
  PackageName,
  determineVersion,
  loadPackage,
  type FoundationGlobal,
  type UserConfig,
} from '@foundation-player/builder';
import type { MediaConfig, PlayerInterface } from '@foundation-player/loader';
import Player from './foundation-ui/components/Player.svelte';
import type { Theme, UIConfig } from './foundation-ui/types/ui-config';
import { UIEvent, type UIEventMap } from './foundation-ui/types/ui-event';
import type { MediaMode } from './shared/types/media-mode';
import type { PlayerLoaderConfig } from './shared/types/player-loader-config';
import ShortsPlayer from './shorts-player/ShortsPlayer.svelte';
import type { ShortsPlayerUI, ShortsPlayerUIConfig } from './shorts-player/types';

export { UIEvent, type UIEventMap };
export type { MediaMode, PlayerLoaderConfig, ShortsPlayerUIConfig, Theme, UIConfig };

type Window = FoundationGlobal & {
  foundation: {
    ui: {
      createPlayerUI: typeof createPlayerUI;
      createShortsPlayerUI: typeof createShortsPlayerUI;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Player?: typeof Player;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ShortsPlayer?: typeof ShortsPlayer;
    };
  };
};

/**
 * Creates a UI for the Foundation Player including all necessary elements:
 * - a fullscreen wrapper (the element to put into fullscreen mode)
 * - a wrapper for non-linear ads
 * - the player element (root element for the Foundation Player)
 * - loading indicator
 * - controls
 *
 * @remarks The markup and styles generated by this function are not part of the
 *    public API. They may change without notice. You should not access its DOM
 *    nodes or modify the styles.
 *
 * @param mountPoint An element into which the UI is inserted.
 * @param loaderConfig The loader config for the Foundation Player. No need to
 *    set the fields `playerElementOrId` or `videoElementOrId`, since the
 *    elements are created by the UI.
 * @param uiConfig Settings for the UI that do not affect the Foundation Player.
 * @return A promise resolving to the created instance of the Foundation Player.
 *    This can then be used to call `loadMedia()`.
 */
export async function createPlayerUI(
  mountPoint: HTMLElement,
  loaderConfig: PlayerLoaderConfig,
  uiConfig: UIConfig = {},
): Promise<PlayerInterface<UIEventMap>> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!mountPoint) {
    throw new Error('Cannot create player UI, because no mount point was given.');
  }

  const isNPM = !(window as unknown as Partial<Window>).foundation?.ui;
  const [version, overwrite] = determineVersion(loaderConfig.version);

  await loadPackage({
    packageName: PackageName.Ui,
    bundleType: BundleType.ES6,
    version,
    isLocal: overwrite ? false : (loaderConfig as UserConfig).isLocal,
    cdn: overwrite ? undefined : (loaderConfig as UserConfig).cdn,
    force: overwrite,
  });

  const foundationGlobal = (window as unknown as Window).foundation;
  if (isNPM) {
    // if the player was included via npm we have to use the newly loaded UI instead
    // to avoid conflicts with different versions of the npm package and the actual runtime version
    return foundationGlobal.ui.createPlayerUI(mountPoint, loaderConfig, uiConfig);
  }

  // Use the Player UI that belongs to the given Foundation Player version.
  // If there is none (older versions) use the bundled UI.
  const playerComponent = new (foundationGlobal.ui.Player || Player)({
    target: mountPoint,
    props: {
      loaderConfig,
      uiConfig,
    },
  });

  return (await playerComponent.getPlayer()) as PlayerInterface<UIEventMap>;
}

/**
 * Creates a UI for the Shorts Player, a TikTok like player for a list of short
 * videos.
 *
 * @remarks The markup and styles generated by this function are not part of the
 *    public API. They may change without notice. You should not access its DOM
 *    nodes or modify the styles.
 *
 * @param mountPoint An element into which the Shorts Player is inserted.
 * @param loaderConfig The loader config for the Foundation Player. No need to
 *    set the fields `playerElementOrId` or `videoElementOrId`, since the
 *    elements are created by the UI.
 * @param mediaConfigs An array of MediaConfigs, one for each video.
 * @param uiConfig Optional settings for the Shorts Player UI.
 * @return A promise resolving to the created instance of the Shorts Player.
 */
export async function createShortsPlayerUI(
  mountPoint: HTMLElement,
  loaderConfig: PlayerLoaderConfig,
  mediaConfigs: MediaConfig[],
  uiConfig: ShortsPlayerUIConfig = {},
): Promise<ShortsPlayerUI> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!mountPoint) {
    throw new Error('Cannot create shorts player UI, because no mount point was given.');
  }

  const isNPM = !(window as unknown as Partial<Window>).foundation?.ui;
  const [version, overwrite] = determineVersion(loaderConfig.version);

  await loadPackage({
    packageName: PackageName.Ui,
    bundleType: BundleType.ES6,
    version,
    isLocal: overwrite ? false : (loaderConfig as UserConfig).isLocal,
    cdn: overwrite ? undefined : (loaderConfig as UserConfig).cdn,
    force: overwrite,
  });

  const foundationGlobal = (window as unknown as Window).foundation;

  if (isNPM) {
    // if the player was included via npm we have to use the newly loaded UI instead
    // to avoid conflicts with different versions of the npm package and the actual runtime version
    return foundationGlobal.ui.createShortsPlayerUI(mountPoint, loaderConfig, mediaConfigs, uiConfig);
  }

  // Use the Shorts Player that belongs to the given Foundation Player version.
  // If there is none (older versions) use the bundled Shorts Player.
  const playerComponent = new (foundationGlobal.ui.ShortsPlayer || ShortsPlayer)({
    target: mountPoint,
    props: {
      loaderConfig,
      mediaConfigs,
      uiConfig,
    },
  });

  return {
    ...playerComponent.api,
    destroy: () => playerComponent.$destroy(),
  };
}

/**
 * @internal
 */
export { Player, ShortsPlayer };
